import React, { useState } from 'react';
import {
  Descriptions,
  Tag,
  Button,
  Space,
  Tabs,
  Card,
  List,
  Badge,
  Typography,
  Collapse,
  Input,
  message,
} from 'antd';
import {
  EditOutlined,
  CodeOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type {
  KrybotTemplate,
  KrybotTemplateVersion,
  KrybotTemplateRenderRequest,
} from '../../../types/krybot';
import { TemplateForm } from './TemplateForm';
import styles from './TemplateManagement.module.css';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

interface TemplateDetailProps {
  template: KrybotTemplate;
  onClose: () => void;
  onRefresh: () => void;
}

export const TemplateDetail: React.FC<TemplateDetailProps> = ({ template, onRefresh }) => {
  const [currentTemplate, setCurrentTemplate] = useState<KrybotTemplate>(template);
  const [showEdit, setShowEdit] = useState(false);
  const [renderVariables, setRenderVariables] = useState<Record<string, unknown>>({});
  const [renderedText, setRenderedText] = useState<string>('');
  const [rendering, setRendering] = useState(false);

  const handleEdit = () => {
    setShowEdit(true);
  };

  const handleSave = async () => {
    setShowEdit(false);
    // Reload template data
    try {
      const updated = await KrybotService.getTemplate(template.template_key);
      setCurrentTemplate(updated);
      onRefresh();
    } catch (err: any) {
      message.error(`Failed to refresh template: ${err.message}`);
    }
  };

  const handleRender = async () => {
    try {
      setRendering(true);
      const request: KrybotTemplateRenderRequest = {
        template_key: currentTemplate.template_key,
        variables: renderVariables,
      };
      const response = await KrybotService.renderTemplate(request);
      setRenderedText(response.rendered_text);
      message.success('Template rendered successfully');
    } catch (err: any) {
      message.error(`Failed to render template: ${err.message}`);
    } finally {
      setRendering(false);
    }
  };

  // Extract template variables (simple regex-based extraction)
  const extractVariables = (text: string): string[] => {
    const matches = text.match(/\{\{(\w+)\}\}/g);
    if (!matches) return [];
    return Array.from(new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, ''))));
  };

  const variables = extractVariables(currentTemplate.prompt_text);

  if (showEdit) {
    return (
      <TemplateForm
        template={currentTemplate}
        onSave={handleSave}
        onCancel={() => setShowEdit(false)}
      />
    );
  }

  return (
    <div className="template-detail">
      <div className="template-detail-header" style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Template Key">{currentTemplate.template_key}</Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag>{currentTemplate.template_type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Sector">
              {currentTemplate.sector || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge
                status={currentTemplate.is_active ? 'success' : 'default'}
                text={currentTemplate.is_active ? 'Active' : 'Inactive'}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Versions">
              {currentTemplate.versions?.length || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              {new Date(currentTemplate.updated_at).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
          <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
            Edit
          </Button>
        </Space>
      </div>

      <Tabs defaultActiveKey="prompt">
        <TabPane tab="Prompt Text" key="prompt">
          <div className="template-detail-section">
            <Card>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {currentTemplate.prompt_text}
              </pre>
            </Card>
          </div>

          {variables.length > 0 && (
            <div className="template-detail-section">
              <h3>Template Variables</h3>
              <Card>
                <List
                  dataSource={variables}
                  renderItem={(varName) => (
                    <List.Item>
                      <Text code>{`{{${varName}}}`}</Text>
                    </List.Item>
                  )}
                />
              </Card>
            </div>
          )}

          {currentTemplate.metadata && (
            <div className="template-detail-section">
              <h3>Metadata</h3>
              <Card>
                <pre className="json-editor">
                  {JSON.stringify(currentTemplate.metadata, null, 2)}
                </pre>
              </Card>
            </div>
          )}
        </TabPane>

        <TabPane tab="Render Preview" key="render">
          <div className="template-detail-section">
            <h3>Test Variables</h3>
            <Card>
              {variables.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {variables.map((varName) => (
                    <div key={varName}>
                      <Text strong>{varName}:</Text>
                      <TextArea
                        rows={2}
                        value={String(renderVariables[varName] || '')}
                        onChange={(e) =>
                          setRenderVariables({ ...renderVariables, [varName]: e.target.value })
                        }
                        placeholder={`Enter value for ${varName}`}
                        style={{ marginTop: 8 }}
                      />
                    </div>
                  ))}
                  <Button type="primary" onClick={handleRender} loading={rendering}>
                    Render Template
                  </Button>
                </Space>
              ) : (
                <Text type="secondary">No variables found in template</Text>
              )}
            </Card>
          </div>

          {renderedText && (
            <div className="template-detail-section">
              <h3>Rendered Output</h3>
              <Card>
                <div className="template-preview">{renderedText}</div>
              </Card>
            </div>
          )}
        </TabPane>

        <TabPane tab="Versions" key="versions">
          <div className="template-version-list">
            <List
              dataSource={currentTemplate.versions || []}
              renderItem={(version: KrybotTemplateVersion) => (
                <List.Item>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Text strong>{version.version_tag}</Text>
                      <Text type="secondary">
                        Created: {new Date(version.created_at).toLocaleString()}
                      </Text>
                    </Space>
                    {version.change_summary && (
                      <Paragraph>{version.change_summary}</Paragraph>
                    )}
                    <Collapse>
                      <Panel header="Version Prompt Text" key="prompt">
                        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                          {version.prompt_text}
                        </pre>
                      </Panel>
                    </Collapse>
                  </Space>
                </List.Item>
              )}
            />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

